import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { invalidateAfterMutation } from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { notificationEndpoints } from "@/features/notification/api/endpoints";
import type {
  SimulateEventResponse,
  SimulateKycCaseApprovedRequest,
} from "@/features/notification/api/dto";

/**
 * Déclenche la notification d'approbation d'un dossier KYC
 * (`POST /NotificationEvents/simulate-kyc-case-approved` — guide §6.7).
 * Rôles : flashpay-superadmin, flashpay-service (compte technique).
 */
export const useSimulateKycCaseApproved = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["notification", "events", "simulate-kyc-case-approved"],
    mutationFn: (body: SimulateKycCaseApprovedRequest) =>
      mutateData({
        url: notificationEndpoints.events.simulateKycCaseApproved,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<SimulateEventResponse>,
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        [["NOTIFICATION_LOGS_LIST"]],
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSimulateKycCaseApproved failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "notification" }
      );
    },
  });
};
